import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PlusSquare, MinusSquare } from '@phosphor-icons/react';
import { PivotReportResponse, ReportDimension, ReportFieldMetadata } from 'shared/types/asset-report';
import PivotDrillDownMenu from './PivotDrillDownMenu';
import { ROW_LEVEL_COLORS, COL_LEVEL_COLORS, getEntityLabel } from '../constants';

interface PivotTableViewProps {
  data: PivotReportResponse;
  rowDimensions: ReportDimension[];
  columnDimensions: ReportDimension[];
  fieldMetadata: ReportFieldMetadata[];
  onAddRowDimension: (dim: ReportDimension, atIndex?: number) => void;
  onRemoveRowDimension: (atIndex: number) => void;
  onAddColumnDimension: (dim: ReportDimension) => void;
  onRemoveColumnDimension: (atIndex: number) => void;
}

const formatNumber = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return '–';
  return new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 2 }).format(value);
};

// ─── Tree node for hierarchical display ──────────────────────────────

interface TreeNode {
  key: string;
  label: string;
  depth: number;
  measures: Record<string, number>;
  columnMeasures: Record<string, Record<string, number>>;
  children: TreeNode[];
}

function buildTree(
  rows: PivotReportResponse['rows'],
  dimLabels: string[],
): TreeNode {
  const root: TreeNode = {
    key: '__root',
    label: 'Totaal',
    depth: -1,
    measures: {},
    columnMeasures: {},
    children: [],
  };

  if (!rows?.length || dimLabels.length === 0) return root;

  for (const row of rows) {
    let current = root;

    // Add to root totals once per row (not per dimension level)
    for (const [ml, mv] of Object.entries(row.totals)) {
      root.measures[ml] = (root.measures[ml] || 0) + mv;
    }
    for (const [colKey, colMeasures] of Object.entries(row.columns)) {
      if (!root.columnMeasures[colKey]) root.columnMeasures[colKey] = {};
      for (const [ml, mv] of Object.entries(colMeasures)) {
        root.columnMeasures[colKey][ml] = (root.columnMeasures[colKey][ml] || 0) + mv;
      }
    }

    for (let d = 0; d < dimLabels.length; d++) {
      const dimLabel = dimLabels[d];
      const value = row.rowKey[dimLabel] || '(Leeg)';

      let child = current.children.find(c => c.label === value);
      if (!child) {
        child = {
          key: `${current.key}/${value}`,
          label: value,
          depth: d,
          measures: {},
          columnMeasures: {},
          children: [],
        };
        current.children.push(child);
      }

      // Accumulate measures on this node
      for (const [ml, mv] of Object.entries(row.totals)) {
        child.measures[ml] = (child.measures[ml] || 0) + mv;
      }

      // Accumulate column measures
      // Accumulate column measures on this node
      for (const [colKey, colMeasures] of Object.entries(row.columns)) {
        if (!child.columnMeasures[colKey]) child.columnMeasures[colKey] = {};
        for (const [ml, mv] of Object.entries(colMeasures)) {
          child.columnMeasures[colKey][ml] = (child.columnMeasures[colKey][ml] || 0) + mv;
        }
      }

      // Only go deeper if more dimensions
      if (d < dimLabels.length - 1) {
        current = child;
      }
    }
  }

  return root;
}

// ─── Component ───────────────────────────────────────────────────────

const PivotTableView: React.FC<PivotTableViewProps> = ({
  data,
  rowDimensions,
  columnDimensions,
  fieldMetadata,
  onAddRowDimension,
  onRemoveRowDimension,
  onAddColumnDimension,
  onRemoveColumnDimension,
}) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [drillMenuAnchor, setDrillMenuAnchor] = useState<{
    position: 'row' | 'column';
    atIndex: number;
    anchorEl: HTMLElement;
    nodeKey?: string; // which node triggered the drill-down
  } | null>(null);

  // Track the node that triggered a drill-down so we can auto-expand only that one
  const pendingExpandRef = useRef<string | null>(null);
  const prevDimCountRef = useRef(rowDimensions.length);

  // When rowDimensions grows (drill-down happened), collapse all first-level nodes
  // except the one that was drilled into
  useEffect(() => {
    if (rowDimensions.length > prevDimCountRef.current && pendingExpandRef.current) {
      // A new dimension was added — collapse all first-level children, expand only the drilled node
      setCollapsed(prev => {
        const next = new Set(prev);
        // We'll add all first-level nodes to collapsed, then remove the drilled one
        // The tree isn't built yet at this point, so we defer to after render
        return next;
      });
    }
    prevDimCountRef.current = rowDimensions.length;
  }, [rowDimensions.length]);

  if (!data?.rows?.length) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-[300px]">
          <p className="text-muted-foreground text-sm">Geen data gevonden voor deze configuratie</p>
        </CardContent>
      </Card>
    );
  }

  const measureLabels = data.rows?.length > 0
    ? Object.keys(data.rows[0].totals)
    : Object.keys(data.grandTotals || {});

  const dimLabels = data.rows?.length > 0 ? Object.keys(data.rows[0].rowKey) : [];
  const hasColumnDims = (data.columnKeys?.length || 0) > 0 && data.columnKeys?.[0] !== '__total';
  const colDimCount = columnDimensions.length;

  // Parse column keys into a tree for hierarchical headers
  // Backend joins multi-level dims with " - ", e.g. "2023 - Account A"
  const columnTree = useMemo(() => {
    if (!hasColumnDims || !data.columnKeys?.length) return null;

    interface ColNode {
      label: string;
      fullKey: string; // the original columnKey for leaf nodes
      children: ColNode[];
      leafCount: number; // number of leaf descendants (for colSpan)
    }

    const root: ColNode = { label: 'root', fullKey: '', children: [], leafCount: 0 };

    for (const colKey of data.columnKeys) {
      const parts = colDimCount > 1 ? colKey.split(' - ') : [colKey];
      let current = root;
      for (let i = 0; i < parts.length; i++) {
        let child = current.children.find(c => c.label === parts[i]);
        if (!child) {
          child = {
            label: parts[i],
            fullKey: i === parts.length - 1 ? colKey : '',
            children: [],
            leafCount: 0,
          };
          current.children.push(child);
        }
        current = child;
      }
    }

    // Calculate leaf counts for colSpan
    const calcLeafCount = (node: ColNode): number => {
      if (node.children.length === 0) {
        node.leafCount = 1;
        return 1;
      }
      node.leafCount = node.children.reduce((sum, c) => sum + calcLeafCount(c), 0);
      return node.leafCount;
    };
    calcLeafCount(root);

    return root;
  }, [data.columnKeys, hasColumnDims, colDimCount]);

  // Build hierarchical tree from flat rows
  const tree = useMemo(() => buildTree(data.rows, dimLabels), [data.rows, dimLabels]);

  // After tree rebuilds from a drill-down, auto-collapse all siblings, expand only the drilled node
  // NEVER collapse __root — Totaal should always be expanded
  useEffect(() => {
    const expandKey = pendingExpandRef.current;
    if (!expandKey || !tree.children.length) return;

    const newCollapsed = new Set<string>();
    for (const child of tree.children) {
      if (child.key !== expandKey) {
        newCollapsed.add(child.key);
      }
    }
    // Ensure root is never collapsed
    newCollapsed.delete('__root');
    setCollapsed(newCollapsed);
    pendingExpandRef.current = null;
  }, [tree]);

  // Ensure root is always expanded on mount and data changes
  useEffect(() => {
    setCollapsed(prev => {
      if (prev.has('__root')) {
        const next = new Set(prev);
        next.delete('__root');
        return next;
      }
      return prev;
    });
  }, [data]);

  const toggleCollapse = (key: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Fields already used as dimensions (to exclude from drill-down menu)
  const usedFields = new Set([
    ...rowDimensions.map(d => d.field),
    ...columnDimensions.map(d => d.field),
  ]);

  // ─── Render tree rows recursively ──────────────────────────────────

  const renderNode = (node: TreeNode, _isLeaf: boolean): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    const isCollapsed = collapsed.has(node.key);
    const hasChildren = node.children.length > 0;
    const indent = (node.depth + 1) * 20;

    rows.push(
      <tr
        key={node.key}
        className="border-b hover:bg-muted/20 transition-colors"
      >
        <td
          className="px-3 py-2 font-medium text-foreground whitespace-nowrap sticky left-0 z-10 bg-background"
          style={{ paddingLeft: `${12 + indent}px` }}
        >
          <div className="flex items-center gap-1.5">
            {/* Expand/Collapse toggle */}
            {hasChildren ? (
              <button
                onClick={() => toggleCollapse(node.key)}
                className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                title={isCollapsed ? 'Uitklappen' : 'Inklappen'}
              >
                {isCollapsed ? (
                  <PlusSquare className="h-4 w-4" weight="bold" />
                ) : (
                  <MinusSquare className="h-4 w-4" weight="bold" />
                )}
              </button>
            ) : (
              // Leaf node — show + to drill down deeper
              <button
                onClick={(e) => {
                  setDrillMenuAnchor({
                    position: 'row',
                    atIndex: node.depth,
                    anchorEl: e.currentTarget,
                    nodeKey: node.depth === 0 ? node.key : undefined,
                  });
                }}
                className="flex-shrink-0 text-primary/50 hover:text-primary transition-colors"
                title="Verder inzoomen"
              >
                <PlusSquare className="h-4 w-4" />
              </button>
            )}

            {/* Colored R-level badge: indicates which dimension THIS value belongs to */}
            {(() => {
              const myLevel = node.depth; // depth 0 = R1 value, depth 1 = R2 value
              const color = ROW_LEVEL_COLORS[myLevel] || ROW_LEVEL_COLORS[ROW_LEVEL_COLORS.length - 1];
              return (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${color.bg} ${color.text}`}>
                  R{myLevel + 1}
                </span>
              );
            })()}

            <span>{node.label}</span>
          </div>
        </td>

        {/* Column dimension values */}
        {hasColumnDims && data.columnKeys?.map(colKey =>
          measureLabels.map(ml => (
            <td key={`${node.key}-${colKey}-${ml}`} className="text-right px-3 py-2 tabular-nums border-l whitespace-nowrap text-sm">
              {formatNumber(node.columnMeasures[colKey]?.[ml])}
            </td>
          ))
        )}

        {/* Row totals */}
        {measureLabels.map(ml => (
          <td
            key={`${node.key}-total-${ml}`}
            className={`text-right px-3 py-2 font-medium tabular-nums border-l whitespace-nowrap text-sm ${
              hasColumnDims ? 'bg-muted/30' : ''
            }`}
          >
            {formatNumber(node.measures[ml])}
          </td>
        ))}
      </tr>,
    );

    // Render children if not collapsed
    if (!isCollapsed && hasChildren) {
      for (const child of node.children) {
        const childIsLeaf = child.children.length === 0;
        rows.push(...renderNode(child, childIsLeaf));
      }
    }

    return rows;
  };

  return (
    <>
      <div
        className="w-full rounded-lg border bg-card text-card-foreground shadow-sm overflow-auto"
        style={{ maxHeight: 'calc(100vh - var(--header-height, 70px) - 60px)' }}
      >
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-30 bg-card shadow-sm">
                  {/* Column headers */}
                  <tr className="border-b bg-muted/50">
                    {/* Simple row header — dimension labels are shown at each tree level */}
                    <th className="text-left px-3 py-2.5 whitespace-nowrap min-w-[250px] sticky left-0 z-20 bg-card">
                      {rowDimensions.length === 0 ? (
                        <span className="text-muted-foreground text-sm italic">Klik ⊞ om te groeperen</span>
                      ) : (
                        <span className="text-sm font-semibold text-foreground">Rijen</span>
                      )}
                    </th>

                    {/* Single-level column headers */}
                    {hasColumnDims && colDimCount <= 1 && data.columnKeys?.map(colKey => (
                      <th
                        key={colKey}
                        colSpan={measureLabels.length}
                        className="text-center font-semibold px-3 py-2.5 text-foreground border-l whitespace-nowrap"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => {
                              setDrillMenuAnchor({
                                position: 'column',
                                atIndex: 0,
                                anchorEl: e.currentTarget,
                              });
                            }}
                            className="text-primary/50 hover:text-primary transition-colors"
                            title="Kolom verder opsplitsen"
                          >
                            <PlusSquare className="h-3.5 w-3.5" />
                          </button>
                          {colKey}
                        </div>
                      </th>
                    ))}

                    {/* Multi-level: only render level 0 (parent groups) in first row */}
                    {hasColumnDims && colDimCount > 1 && columnTree?.children.map(node => (
                      <th
                        key={`col-0-${node.label}`}
                        colSpan={node.leafCount * measureLabels.length}
                        className="text-center font-semibold px-3 py-2 text-foreground border-l whitespace-nowrap"
                      >
                        {node.label}
                      </th>
                    ))}

                    {/* Totals column header — with column dimension labels or + picker */}
                    <th
                      colSpan={measureLabels.length}
                      className="text-center px-3 py-2.5 text-foreground border-l bg-muted whitespace-nowrap"
                    >
                      {!hasColumnDims ? (
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={(e) => {
                              setDrillMenuAnchor({
                                position: 'column',
                                atIndex: -1,
                                anchorEl: e.currentTarget,
                              });
                            }}
                            className="text-primary/50 hover:text-primary transition-colors"
                            title="Kolommen toevoegen"
                          >
                            <PlusSquare className="h-3.5 w-3.5" />
                          </button>
                          <span className="font-semibold">Totaal</span>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex items-center gap-1 flex-wrap justify-center">
                            {columnDimensions.map((cd, ci) => {
                              const color = COL_LEVEL_COLORS[ci] || COL_LEVEL_COLORS[COL_LEVEL_COLORS.length - 1];
                              return (
                                <span
                                  key={cd.field}
                                  className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded ${color.bg} ${color.text}`}
                                >
                                  <span className="font-bold">{color.label}</span>
                                  {getEntityLabel(cd.field, cd.label)}
                                  <button
                                    onClick={() => onRemoveColumnDimension(ci)}
                                    className="ml-0.5 hover:opacity-70 transition-opacity rounded-full"
                                    title={`${cd.label} verwijderen`}
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                  </button>
                                </span>
                              );
                            })}
                          </div>
                          <span className="text-xs text-muted-foreground font-medium">Totaal</span>
                        </div>
                      )}
                    </th>
                  </tr>

                  {/* Multi-level columns: render deeper levels as separate header rows */}
                  {hasColumnDims && colDimCount > 1 && columnTree && (() => {
                    // Collect all levels beyond 0
                    const deeperLevels: React.ReactNode[][] = [];
                    const collectDeeper = (nodes: typeof columnTree.children, depth: number) => {
                      for (const node of nodes) {
                        if (node.children.length > 0) {
                          if (!deeperLevels[depth]) deeperLevels[depth] = [];
                          for (const child of node.children) {
                            const isLeaf = child.children.length === 0;
                            const span = (isLeaf ? 1 : child.leafCount) * measureLabels.length;
                            deeperLevels[depth].push(
                              <th
                                key={`col-${depth + 1}-${node.label}-${child.label}`}
                                colSpan={span}
                                className="text-center font-medium text-sm px-2 py-1.5 text-foreground border-l whitespace-nowrap"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  {isLeaf && (
                                    <button
                                      onClick={(e) => {
                                        setDrillMenuAnchor({
                                          position: 'column',
                                          atIndex: depth + 1,
                                          anchorEl: e.currentTarget,
                                        });
                                      }}
                                      className="text-primary/50 hover:text-primary transition-colors"
                                      title="Kolom verder opsplitsen"
                                    >
                                      <PlusSquare className="h-3 w-3" />
                                    </button>
                                  )}
                                  {child.label}
                                </div>
                              </th>,
                            );
                            if (!isLeaf) {
                              collectDeeper([child], depth + 1);
                            }
                          }
                        }
                      }
                    };
                    collectDeeper(columnTree.children, 0);

                    return deeperLevels.map((cells, lvl) => (
                      <tr key={`col-sublevel-${lvl}`} className="border-b bg-muted/40">
                        <th className="sticky left-0 z-20 bg-card" />
                        {cells}
                        <th colSpan={measureLabels.length} className="border-l bg-muted/50" />
                      </tr>
                    ));
                  })()}

                  {/* Measure sub-headers */}
                  {(measureLabels.length > 1 || hasColumnDims) && (
                    <tr className="border-b bg-muted/30">
                      <th className="px-3 py-1.5 sticky left-0 z-20 bg-card" />
                      {hasColumnDims && data.columnKeys?.map(colKey =>
                        measureLabels.map(ml => (
                          <th key={`${colKey}-${ml}`} className="text-right font-medium text-xs px-3 py-1.5 text-muted-foreground border-l whitespace-nowrap">
                            {ml}
                          </th>
                        ))
                      )}
                      {measureLabels.map(ml => (
                        <th key={`total-${ml}`} className="text-right font-medium text-xs px-3 py-1.5 text-muted-foreground border-l bg-muted/50 whitespace-nowrap">
                          {ml}
                        </th>
                      ))}
                    </tr>
                  )}
                </thead>

                <tbody>
                  {/* Grand total row — shows dimension name + × at this level */}
                  <tr className="border-b bg-muted/50 font-semibold">
                    <td className="px-3 py-2.5 text-foreground sticky left-0 z-10 bg-card">
                      <div className="flex items-center gap-1.5">
                        {rowDimensions.length > 0 ? (
                          <>
                            <button
                              onClick={() => toggleCollapse('__root')}
                              className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {collapsed.has('__root') ? (
                                <PlusSquare className="h-4 w-4" weight="bold" />
                              ) : (
                                <MinusSquare className="h-4 w-4" weight="bold" />
                              )}
                            </button>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${ROW_LEVEL_COLORS[0].bg} ${ROW_LEVEL_COLORS[0].text}`}>
                              R1
                            </span>
                            <span className="font-semibold">{getEntityLabel(rowDimensions[0].field, rowDimensions[0].label)}</span>
                            <button
                              onClick={() => onRemoveRowDimension(0)}
                              className="flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                              title={`${rowDimensions[0].label} verwijderen`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={(e) => {
                                setDrillMenuAnchor({
                                  position: 'row',
                                  atIndex: -1,
                                  anchorEl: e.currentTarget,
                                });
                              }}
                              className="flex-shrink-0 text-primary/50 hover:text-primary transition-colors"
                              title="Kies een dimensie om op te groeperen"
                            >
                              <PlusSquare className="h-4 w-4" weight="bold" />
                            </button>
                            <span>Totaal</span>
                          </>
                        )}
                      </div>
                    </td>

                    {hasColumnDims && data.columnKeys?.map(colKey =>
                      measureLabels.map(ml => (
                        <td key={`grand-${colKey}-${ml}`} className="text-right px-3 py-2.5 tabular-nums border-l whitespace-nowrap text-sm">
                          {formatNumber(tree.columnMeasures[colKey]?.[ml])}
                        </td>
                      ))
                    )}

                    {measureLabels.map(ml => (
                      <td key={`grand-total-${ml}`} className="text-right px-3 py-2.5 tabular-nums border-l bg-muted whitespace-nowrap text-sm">
                        {formatNumber(tree.measures[ml] || data.grandTotals?.[ml])}
                      </td>
                    ))}
                  </tr>

                  {/* Tree rows */}
                  {!collapsed.has('__root') && tree.children.map(child =>
                    renderNode(child, child.children.length === 0)
                  )}
                </tbody>
              </table>
      </div>

      {/* Drill-down menu */}
      {drillMenuAnchor && (
        <PivotDrillDownMenu
          anchorEl={drillMenuAnchor.anchorEl}
          position={drillMenuAnchor.position}
          fieldMetadata={fieldMetadata}
          usedFields={usedFields}
          onSelect={(dim) => {
            if (drillMenuAnchor.position === 'row') {
              // Remember which node to auto-expand after data refreshes
              if (drillMenuAnchor.nodeKey) {
                pendingExpandRef.current = drillMenuAnchor.nodeKey;
              }
              onAddRowDimension(dim, drillMenuAnchor.atIndex);
            } else {
              onAddColumnDimension(dim);
            }
            setDrillMenuAnchor(null);
          }}
          onClose={() => setDrillMenuAnchor(null)}
        />
      )}
    </>
  );
};

export default PivotTableView;
